import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import {
  Sparkles, ArrowRight, HandCoins, Users, Rocket, Clock, ClipboardCheck,
  Wallet, Link2, ShieldCheck,
} from "lucide-react";

/**
 * Public Affiliate Program marketing page.
 *
 * Route: /affiliates (public, no login required, index,follow).
 * CTA routes to the existing authenticated apply page at /affiliate-program,
 * which mounts the current AffiliateProgram.jsx apply/status component. No
 * new affiliate backend was created — this page is the customer-facing
 * marketing surface only. Every program term below is pulled verbatim
 * from /app/backend/affiliate.py (COMMISSION_RATE_BPS, COMMISSION_DURATION_DAYS,
 * ATTRIBUTION_WINDOW_DAYS, COMMISSION_HOLD_DAYS, MIN_PAYOUT_CENTS).
 */
export default function AffiliatesPublicPage() {
  return (
    <>
      <SEO
        title="Postmoro Affiliate Program | Earn by Referring Businesses"
        description="Join the Postmoro Affiliate Program and earn commissions by referring businesses to Postmoro's AI social media management platform."
        path="/affiliates"
      />

      <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)]" data-testid="affiliates-page">
        <MarketingHeader activePath="/affiliates" />

        {/* --------- HERO --------- */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]">
              <HandCoins className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
              Postmoro Affiliate Program
            </div>
            <h1
              className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]"
              data-testid="affiliates-hero-title"
            >
              Earn With Postmoro
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl">
              Recommend Postmoro to businesses, creators, agencies, and entrepreneurs
              and earn commission on qualifying referrals. Approved affiliates get
              a unique referral link and a live dashboard for clicks, referrals,
              and pending / approved commission.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/affiliate-program">
                <Button
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                  data-testid="affiliates-hero-apply"
                >
                  Apply to Become an Affiliate <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link
                to="#how-it-works"
                className="text-sm text-[color:var(--brand-primary)] hover:underline"
                data-testid="affiliates-hero-how"
              >
                See how it works →
              </Link>
            </div>
            <div className="mt-4 text-xs text-[color:var(--text-3)]">
              Manual approval required · 50% commission for the first 12 months · Monthly payouts
            </div>
          </div>
        </section>

        {/* --------- COMMISSION HIGHLIGHTS --------- */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="affiliates-terms">
            {[
              { icon: HandCoins,     label: "Commission",   value: "50%",         sub: "of qualifying referred subscriptions" },
              { icon: Clock,         label: "For",          value: "12 months",   sub: "per referred customer" },
              { icon: Link2,         label: "Attribution",  value: "60 days",     sub: "referral cookie window" },
              { icon: Wallet,        label: "Payouts",      value: "Monthly",     sub: "$50 minimum · 30-day hold" },
            ].map((t) => (
              <div
                key={t.label}
                className="relative rounded-2xl glass p-5 overflow-hidden"
                data-testid={`affiliates-term-${t.label.toLowerCase()}`}
              >
                <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[2px]" style={{ background: "linear-gradient(90deg,#1BA89F,#22D3EE)" }} />
                <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.14)] text-[color:var(--brand-primary)] flex items-center justify-center">
                  <t.icon className="h-4.5 w-4.5" />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)] mt-3">{t.label}</div>
                <div className="font-display text-2xl font-semibold mt-1">{t.value}</div>
                <div className="text-xs text-[color:var(--text-2)] mt-1">{t.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* --------- HOW IT WORKS --------- */}
        <section
          id="how-it-works"
          className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14"
          data-testid="affiliates-how-it-works"
        >
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">How it works</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Four Simple Steps To Start Earning
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: "01", icon: ClipboardCheck, title: "Apply",           body: "Submit your affiliate application." },
              { n: "02", icon: ShieldCheck,    title: "Get Approved",    body: "Postmoro reviews applications manually." },
              { n: "03", icon: Link2,          title: "Share Your Link", body: "Approved affiliates get a unique referral link and dashboard." },
              { n: "04", icon: HandCoins,      title: "Earn Commission", body: "Qualifying paid referrals are tracked and commissions follow the program terms." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl glass p-5 relative overflow-hidden">
                <div className="text-[10px] font-mono text-[color:var(--text-3)] tracking-wider">{s.n}</div>
                <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.14)] text-[color:var(--brand-primary)] flex items-center justify-center mt-2">
                  <s.icon className="h-4.5 w-4.5" />
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold">{s.title}</h3>
                <p className="mt-1.5 text-sm text-[color:var(--text-2)] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* --------- WHO IT'S FOR --------- */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14" data-testid="affiliates-audience">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-8 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Who it's for</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                Built For People With An Audience
              </h2>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed">
                If you already talk to small business owners, creators, agencies, or
                marketers, Postmoro is a natural recommendation. You share the link.
                Qualifying paid referrals earn you commission for the first 12 months.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: Users,   title: "Creators & Newsletters",   body: "Sharing tools your audience already asks about." },
                { icon: Rocket,  title: "Agencies & Consultants",   body: "Recommending Postmoro to clients running social." },
                { icon: Sparkles,title: "Community Builders",       body: "Groups, communities, and directories with small-business owners." },
                { icon: HandCoins,title:"Marketers & Educators",    body: "Content, courses, and comparison round-ups." },
              ].map((a) => (
                <div key={a.title} className="rounded-2xl glass p-5">
                  <div className="h-8 w-8 rounded-md bg-[color:rgba(27,168,159,0.14)] text-[color:var(--brand-primary)] flex items-center justify-center">
                    <a.icon className="h-4 w-4" />
                  </div>
                  <div className="font-display text-base font-semibold mt-3">{a.title}</div>
                  <div className="text-xs text-[color:var(--text-2)] mt-1 leading-relaxed">{a.body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --------- FAQ --------- */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14" data-testid="affiliates-faq">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">FAQ</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Everything About The Program
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { q: "How much can I earn?",             a: "50% commission for the first 12 months on qualifying referred subscriptions." },
              { q: "How long does attribution last?",  a: "60 days. If someone clicks your link and signs up within 60 days, they are attributed to you." },
              { q: "When are commissions payable?",    a: "After a 30-day hold from the qualifying invoice, and paid through the monthly payout process." },
              { q: "Is there a minimum payout?",       a: "Yes, $50. Balances below the minimum roll over into the next monthly cycle." },
              { q: "Does everyone get accepted?",      a: "No — affiliate applications are manually reviewed by Postmoro. We look for a real audience, honest positioning, and no spammy channels." },
              { q: "How do I track referrals?",        a: "Approved affiliates get a unique referral link and a live dashboard showing clicks, referrals, and pending / approved / paid commission." },
              { q: "Are refunds and chargebacks reversed?", a: "Yes. If a referred customer refunds or chargebacks, the corresponding commission is reversed automatically." },
              { q: "Can I refer myself?",              a: "No. Self-referrals are blocked at both signup attribution and invoice-commission time." },
            ].map((f) => (
              <div key={f.q} className="rounded-2xl glass p-5">
                <div className="font-display text-base font-semibold">{f.q}</div>
                <div className="mt-1.5 text-sm text-[color:var(--text-2)] leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </section>

        {/* --------- FINAL CTA --------- */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-4 pb-20" data-testid="affiliates-final-cta">
          <div className="rounded-3xl glass p-8 sm:p-12 text-center relative overflow-hidden">
            <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(27,168,159,0.24), transparent 70%)" }} />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Ready?</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                Start Earning With Postmoro
              </h2>
              <p className="mt-3 text-[color:var(--text-2)] max-w-xl mx-auto">
                Apply in a couple of minutes. Postmoro reviews every application manually so we can keep the program clean and high-signal.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link to="/affiliate-program">
                  <Button
                    className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                    data-testid="affiliates-final-apply"
                  >
                    Apply to Become an Affiliate <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/pricing" className="text-sm text-[color:var(--brand-primary)] hover:underline">
                  See Postmoro pricing →
                </Link>
              </div>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
